import type { AthenaResult } from "./client-result.ts";
import { AthenaGatewayError, isAthenaGatewayError } from "./gateway/errors.ts";
import type {
  AthenaGatewayErrorCode,
  AthenaGatewayErrorDetails,
} from "./gateway/types.ts";
import { classifyAthenaError } from "./error/classify.ts";
import {
  AthenaErrorCategory,
  AthenaErrorCode,
  AthenaErrorKind,
  type AthenaOperationContext,
  type NormalizedAthenaError,
} from "./error/types.ts";
import { parseBooleanFlag as parseBooleanFlagUtil } from "./utils/parse-boolean-flag.ts";
export type {
  AthenaErrorInput,
  AthenaOperationContext,
  NormalizedAthenaError,
} from "./error/types.ts";
export {
  AthenaError,
  AthenaErrorCategory,
  AthenaErrorCode,
  AthenaErrorKind,
} from "./error/types.ts";
export interface UnwrapOptions {
  allowNull?: boolean;
  context?: AthenaOperationContext;
}

export interface UnwrapOneOptions extends UnwrapOptions {
  requireExactlyOne?: boolean;
}

export interface IntCoercionOptions {
  max?: number;
  min?: number;
  strictBigInt?: boolean;
}

export interface RequireAffectedOptions {
  min?: number;
}

/**
 * Parses a string-based boolean flag with a deterministic fallback.
 *
 * Accepts common truthy/falsey token variants used by env vars and CLI flags.
 */
export function parseBooleanFlag(
  rawValue: string | undefined,
  fallback: boolean
): boolean {
  return parseBooleanFlagUtil(rawValue, fallback);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
}

function messageFromUnknownError(error: unknown): string | undefined {
  if (typeof error === "string" && error.trim().length > 0) {
    return error.trim();
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim();
  }
  if (!isRecord(error)) {
    return;
  }
  return firstNonEmptyString(error.message, error.error, error.details);
}

function gatewayCodeFromUnknownError(
  error: unknown
): AthenaGatewayErrorCode | undefined {
  if (!isRecord(error) || typeof error.gatewayCode !== "string") {
    return;
  }
  return error.gatewayCode as AthenaGatewayErrorCode;
}

function isAthenaResultErrorLike(value: unknown): value is {
  message: string;
  athenaCode?: AthenaErrorCode;
  kind?: AthenaErrorKind;
  category?: AthenaErrorCategory;
  retryable?: boolean;
  status?: number;
  constraint?: string;
  table?: string;
  operation?: string;
  raw?: unknown;
} {
  return (
    isRecord(value) &&
    typeof value.message === "string" &&
    (value.athenaCode === undefined || isAthenaErrorCode(value.athenaCode)) &&
    (value.kind === undefined || isAthenaErrorKind(value.kind)) &&
    (value.category === undefined || isAthenaErrorCategory(value.category)) &&
    (value.retryable === undefined || typeof value.retryable === "boolean") &&
    (value.status === undefined || typeof value.status === "number")
  );
}

function isAthenaErrorKind(value: unknown): value is AthenaErrorKind {
  return (
    value === "unique_violation" ||
    value === "not_found" ||
    value === "validation" ||
    value === "auth" ||
    value === "rate_limit" ||
    value === "transient" ||
    value === "unknown"
  );
}

function isAthenaErrorCode(value: unknown): value is AthenaErrorCode {
  return (
    value === "UNIQUE_VIOLATION" ||
    value === "NOT_FOUND" ||
    value === "VALIDATION_FAILED" ||
    value === "AUTH_UNAUTHORIZED" ||
    value === "AUTH_FORBIDDEN" ||
    value === "RATE_LIMITED" ||
    value === "NETWORK_UNAVAILABLE" ||
    value === "TRANSIENT_FAILURE" ||
    value === "HTTP_FAILURE" ||
    value === "UNKNOWN"
  );
}

function isAthenaErrorCategory(value: unknown): value is AthenaErrorCategory {
  return (
    value === "transport" ||
    value === "client" ||
    value === "server" ||
    value === "database" ||
    value === "unknown"
  );
}

function isNormalizedAthenaError(
  value: unknown
): value is NormalizedAthenaError {
  return (
    isRecord(value) &&
    isAthenaErrorKind(value.kind) &&
    isAthenaErrorCode(value.code) &&
    isAthenaErrorCategory(value.category) &&
    typeof value.retryable === "boolean" &&
    typeof value.message === "string" &&
    "raw" in value
  );
}

function withContextOverrides(
  normalized: NormalizedAthenaError,
  context?: AthenaOperationContext
): NormalizedAthenaError {
  if (!(context?.table || context?.operation)) {
    return normalized;
  }

  return {
    ...normalized,
    operation: context.operation ?? normalized.operation,
    table: context.table ?? normalized.table,
  };
}

function resolveAttachedNormalizedError(
  resultOrError: unknown
): NormalizedAthenaError | undefined {
  if (!isRecord(resultOrError)) {
    return;
  }
  if (!("__athenaNormalizedError" in resultOrError)) {
    return;
  }
  const candidate = resultOrError.__athenaNormalizedError;
  return isNormalizedAthenaError(candidate) ? candidate : undefined;
}

function safeStringify(value: unknown): string {
  try {
    const serialized = JSON.stringify(value);
    return serialized ?? String(value);
  } catch {
    return "[unserializable]";
  }
}

function contextHint(context?: AthenaOperationContext): string | undefined {
  if (!context?.identity) {
    return;
  }
  const identity =
    typeof context.identity === "string"
      ? context.identity
      : safeStringify(context.identity);
  return `Identity: ${identity}`;
}

function isAthenaResultLike(value: unknown): value is AthenaResult<unknown> {
  return (
    isRecord(value) &&
    "status" in value &&
    "error" in value &&
    "data" in value &&
    typeof value.status === "number"
  );
}

function operationFromDetails(
  details?: AthenaGatewayErrorDetails | null
): string | undefined {
  if (!details?.endpoint) {
    return;
  }
  if (
    details.endpoint === "/gateway/fetch" ||
    details.endpoint === "/gateway/query"
  ) {
    return "select";
  }
  if (details.endpoint === "/gateway/insert") {
    return "insert";
  }
  if (details.endpoint === "/gateway/update") {
    return "update";
  }
  if (details.endpoint === "/gateway/delete") {
    return "delete";
  }
  if (
    details.endpoint === "/gateway/rpc" ||
    details.endpoint.startsWith("/rpc/")
  ) {
    return "rpc";
  }
}

function matchRegex(input: string, patterns: RegExp[]): string | undefined {
  for (const pattern of patterns) {
    const match = pattern.exec(input);
    if (match?.[1]) {
      return match[1];
    }
  }
}

function extractConstraint(message: string): string | undefined {
  return matchRegex(message, [
    /unique constraint\s+["'`]([^"'`]+)["'`]/i,
    /constraint\s+["'`]([^"'`]+)["'`]/i,
  ]);
}

function extractTable(message: string): string | undefined {
  return matchRegex(message, [
    /(?:table|relation)\s+["'`]([^"'`]+)["'`]/i,
    /on\s+table\s+([a-zA-Z0-9_.]+)/i,
  ]);
}

function toAthenaErrorCode(
  kind: AthenaErrorKind,
  status: number | undefined,
  gatewayCode?: AthenaGatewayErrorCode
): AthenaErrorCode {
  if (gatewayCode === "INVALID_URL") {
    return AthenaErrorCode.ValidationFailed;
  }

  if (
    gatewayCode === "NETWORK_ERROR" ||
    (kind === "transient" && status === 0)
  ) {
    return AthenaErrorCode.NetworkUnavailable;
  }

  switch (kind) {
    case "unique_violation":
      return AthenaErrorCode.UniqueViolation;
    case "not_found":
      return AthenaErrorCode.NotFound;
    case "validation":
      return AthenaErrorCode.ValidationFailed;
    case "rate_limit":
      return AthenaErrorCode.RateLimited;
    case "auth":
      if (status === 403) {
        return AthenaErrorCode.AuthForbidden;
      }
      return AthenaErrorCode.AuthUnauthorized;
    case "transient":
      return status !== undefined && status >= 500
        ? AthenaErrorCode.HttpFailure
        : AthenaErrorCode.TransientFailure;
    default:
      return status !== undefined && status >= 400
        ? AthenaErrorCode.HttpFailure
        : AthenaErrorCode.Unknown;
  }
}

function toAthenaErrorCategory(
  kind: AthenaErrorKind,
  status: number | undefined
): AthenaErrorCategory {
  if (kind === "transient" && (status === 0 || status === undefined)) {
    return AthenaErrorCategory.Transport;
  }
  if (kind === "unique_violation") {
    return AthenaErrorCategory.Database;
  }
  if (kind === "validation" || kind === "auth" || kind === "not_found") {
    return AthenaErrorCategory.Client;
  }
  if (kind === "rate_limit" || kind === "transient") {
    return AthenaErrorCategory.Server;
  }
  return AthenaErrorCategory.Unknown;
}

function isRetryable(
  kind: AthenaErrorKind,
  status: number | undefined
): boolean {
  if (kind === "rate_limit" || kind === "transient") {
    return true;
  }
  return status !== undefined && status >= 500;
}

function toGatewayCode(
  kind: AthenaErrorKind,
  status?: number
): AthenaGatewayErrorCode {
  if (kind === "transient" && (status === 0 || status === undefined)) {
    return "NETWORK_ERROR";
  }
  if (kind === "validation") {
    return "INVALID_JSON";
  }
  if (status !== undefined && status >= 400) {
    return "HTTP_ERROR";
  }
  return "UNKNOWN_ERROR";
}

export function toAthenaGatewayError(
  source: unknown,
  fallbackMessage: string,
  context?: AthenaOperationContext
): AthenaGatewayError {
  if (isAthenaGatewayError(source)) {
    return source;
  }

  if (isAthenaResultLike(source) && source.errorDetails) {
    const message =
      messageFromUnknownError(source.error) ??
      source.errorDetails.message ??
      fallbackMessage;
    return new AthenaGatewayError({
      cause:
        (isRecord(source.error)
          ? firstNonEmptyString(source.error.cause)
          : undefined) ?? source.errorDetails.cause,
      code: source.errorDetails.code,
      endpoint: source.errorDetails.endpoint,
      hint:
        (isRecord(source.error)
          ? firstNonEmptyString(source.error.hint)
          : undefined) ?? source.errorDetails.hint,
      message,
      method: source.errorDetails.method,
      requestId: source.errorDetails.requestId,
      status: source.status,
    });
  }

  const normalized = normalizeAthenaError(source, context);
  const message =
    isAthenaResultLike(source) &&
    source.error === null &&
    source.errorDetails === null
      ? fallbackMessage
      : normalized.message || fallbackMessage;

  return new AthenaGatewayError({
    cause:
      typeof normalized.raw === "string"
        ? normalized.raw
        : safeStringify(normalized.raw),
    code: toGatewayCode(normalized.kind, normalized.status),
    hint:
      normalized.constraint === null
        ? contextHint(context)
        : `Constraint: ${normalized.constraint}`,
    message,
    status: normalized.status ?? 0,
  });
}

/**
 * Returns `true` when a result is successful (`2xx` status and no `error`).
 */
export function isOk<T>(result: AthenaResult<T>): boolean {
  return result.error === null && result.status >= 200 && result.status < 300;
}

/**
 * @deprecated Prefer `result.error` on failed `AthenaResult` values and the
 * structured fields already attached to thrown SDK errors. This helper is
 * retained for compatibility with mixed unknown inputs.
 *
 * Normalizes any Athena failure shape into a stable, typed error envelope.
 *
 * Accepts `AthenaResult`, `AthenaGatewayError`, native `Error`, or unknown values.
 * Optional `context` can override inferred table/operation metadata for clearer diagnostics.
 */
export function normalizeAthenaError(
  resultOrError: unknown,
  context?: AthenaOperationContext
): NormalizedAthenaError {
  const attached = resolveAttachedNormalizedError(resultOrError);
  if (attached) {
    return withContextOverrides(attached, context);
  }

  if (isAthenaResultLike(resultOrError)) {
    if (isAthenaResultErrorLike(resultOrError.error)) {
      return {
        category:
          resultOrError.error.category ??
          toAthenaErrorCategory(
            resultOrError.error.kind ??
              classifyAthenaError(
                resultOrError.status,
                gatewayCodeFromUnknownError(resultOrError.error),
                resultOrError.error.message
              ),
            resultOrError.error.status ?? resultOrError.status
          ),
        code:
          resultOrError.error.athenaCode ??
          toAthenaErrorCode(
            resultOrError.error.kind ??
              classifyAthenaError(
                resultOrError.status,
                gatewayCodeFromUnknownError(resultOrError.error),
                resultOrError.error.message
              ),
            resultOrError.error.status ?? resultOrError.status,
            gatewayCodeFromUnknownError(resultOrError.error)
          ),
        constraint: resultOrError.error.constraint,
        kind:
          resultOrError.error.kind ??
          classifyAthenaError(
            resultOrError.status,
            gatewayCodeFromUnknownError(resultOrError.error),
            resultOrError.error.message
          ),
        message: resultOrError.error.message,
        operation: context?.operation ?? resultOrError.error.operation,
        raw: resultOrError.error.raw ?? resultOrError.raw,
        retryable:
          resultOrError.error.retryable ??
          isRetryable(
            resultOrError.error.kind ??
              classifyAthenaError(
                resultOrError.status,
                gatewayCodeFromUnknownError(resultOrError.error),
                resultOrError.error.message
              ),
            resultOrError.error.status ?? resultOrError.status
          ),
        status: resultOrError.error.status ?? resultOrError.status,
        table: context?.table ?? resultOrError.error.table,
      };
    }

    const details = resultOrError.errorDetails;
    const message =
      messageFromUnknownError(resultOrError.error) ??
      details?.message ??
      `Athena ${context?.operation ?? operationFromDetails(details) ?? "request"} failed`;
    const operation = context?.operation ?? operationFromDetails(details);
    const table = context?.table ?? extractTable(message);
    const constraint = extractConstraint(message);
    const gatewayCode =
      details?.code ?? gatewayCodeFromUnknownError(resultOrError.error);
    const kind = classifyAthenaError(resultOrError.status, gatewayCode, message);
    const code = toAthenaErrorCode(kind, resultOrError.status, gatewayCode);
    const category = toAthenaErrorCategory(kind, resultOrError.status);
    return {
      category,
      code,
      constraint,
      kind,
      message,
      operation,
      raw: resultOrError.raw,
      retryable: isRetryable(kind, resultOrError.status),
      status: resultOrError.status,
      table,
    };
  }

  if (isAthenaGatewayError(resultOrError)) {
    const details = resultOrError.toDetails();
    const operation = context?.operation ?? operationFromDetails(details);
    const table = context?.table ?? extractTable(resultOrError.message);
    const constraint = extractConstraint(resultOrError.message);
    const kind = classifyAthenaError(
      resultOrError.status,
      resultOrError.code,
      resultOrError.message
    );
    const code = toAthenaErrorCode(
      kind,
      resultOrError.status,
      resultOrError.code
    );
    const category = toAthenaErrorCategory(kind, resultOrError.status);
    return {
      category,
      code,
      constraint,
      kind,
      message: resultOrError.message,
      operation,
      raw: resultOrError,
      retryable: isRetryable(kind, resultOrError.status),
      status: resultOrError.status,
      table,
    };
  }

  if (resultOrError instanceof Error) {
    const maybeStatus =
      isRecord(resultOrError) && typeof resultOrError.status === "number"
        ? resultOrError.status
        : undefined;
    const kind = classifyAthenaError(maybeStatus, undefined, resultOrError.message);
    return {
      category: toAthenaErrorCategory(kind, maybeStatus),
      code: toAthenaErrorCode(kind, maybeStatus),
      constraint: extractConstraint(resultOrError.message),
      kind,
      message: resultOrError.message,
      operation: context?.operation,
      raw: resultOrError,
      retryable: isRetryable(kind, maybeStatus),
      status: maybeStatus,
      table: context?.table ?? extractTable(resultOrError.message),
    };
  }

  const message =
    typeof resultOrError === "string" ? resultOrError : "Unknown Athena error";
  const kind = classifyAthenaError(undefined, undefined, message);
  return {
    category: toAthenaErrorCategory(kind, undefined),
    code: toAthenaErrorCode(kind, undefined),
    constraint: extractConstraint(message),
    kind,
    message,
    operation: context?.operation,
    raw: resultOrError,
    retryable: isRetryable(kind, undefined),
    status: undefined,
    table: context?.table ?? extractTable(message),
  };
}

/**
 * Unwraps a successful result into a row array.
 *
 * - Throws on failed results.
 * - Converts `null` data to an empty array.
 * - Wraps scalar data in a single-element array.
 */
export function unwrapRows<T>(
  result: AthenaResult<T[] | T | null>,
  options?: UnwrapOptions
): T[] {
  if (!isOk(result)) {
    throw toAthenaGatewayError(
      result,
      "Athena request failed",
      options?.context
    );
  }
  if (result.data === null) {
    return [];
  }
  return Array.isArray(result.data) ? result.data : [result.data];
}

/**
 * Unwraps successful result data from `AthenaResult<T | null>`.
 *
 * By default, `null` data throws. Pass `{ allowNull: true }` to permit nullable payloads.
 */
export function unwrap<T>(
  result: AthenaResult<T | null>,
  options: UnwrapOptions & { allowNull: true }
): T | null;
export function unwrap<T>(
  result: AthenaResult<T | null>,
  options?: UnwrapOptions
): T;
export function unwrap<T>(
  result: AthenaResult<T | null>,
  options?: UnwrapOptions
): T | null {
  if (!isOk(result)) {
    throw toAthenaGatewayError(
      result,
      "Athena request failed",
      options?.context
    );
  }
  if (result.data === null && !options?.allowNull) {
    throw toAthenaGatewayError(
      result,
      "Expected data but received null",
      options?.context
    );
  }
  return result.data;
}

/**
 * Unwraps the first row from a successful result that may contain arrays/scalars/null.
 *
 * - Throws on failed results.
 * - Throws when no row exists unless `allowNull: true` is provided.
 * - Optionally enforces exact cardinality via `requireExactlyOne`.
 */
export function unwrapOne<T>(
  result: AthenaResult<T[] | T | null>,
  options: UnwrapOneOptions & { allowNull: true }
): T | null;
export function unwrapOne<T>(
  result: AthenaResult<T[] | T | null>,
  options?: UnwrapOneOptions
): T;
export function unwrapOne<T>(
  result: AthenaResult<T[] | T | null>,
  options?: UnwrapOneOptions
): T | null {
  const rows = unwrapRows(result, options);
  if (!rows.length) {
    if (options?.allowNull) {
      return null;
    }
    throw toAthenaGatewayError(
      result,
      "Expected one row but received none",
      options?.context
    );
  }
  if (options?.requireExactlyOne && rows.length !== 1) {
    throw toAthenaGatewayError(
      result,
      `Expected exactly one row but received ${rows.length}`,
      options.context
    );
  }
  return rows[0];
}

/**
 * Asserts that an Athena result is successful.
 *
 * Returns the original result for fluent composition and throws `AthenaGatewayError` on failure.
 */
export function requireSuccess<T>(
  result: AthenaResult<T>,
  context?: AthenaOperationContext
): AthenaResult<T> {
  if (!isOk(result)) {
    throw toAthenaGatewayError(
      result,
      `Athena ${context?.operation ?? "request"} failed`,
      context
    );
  }
  return result;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Enforces mutation postconditions from the canonical row-count:
 * numeric `result.count`, else numeric `result.affectedRows`.
 *
 * - Validates success first.
 * - Does not require `{ count: "exact" }` on PG/D1 (driver meta already
 *   populates `count` / `affectedRows`).
 * - When `options.min` is set, validates resolved count `>= min`.
 *   A CAS miss (`0`) is a successful read of the count, not a missing field.
 */
export function requireAffected<T>(
  result: AthenaResult<T>,
  options?: RequireAffectedOptions,
  context?: AthenaOperationContext
): number {
  requireSuccess(result, context);

  const count = isFiniteNumber(result.count)
    ? result.count
    : isFiniteNumber(result.affectedRows)
      ? result.affectedRows
      : null;
  if (count === null) {
    throw new AthenaGatewayError({
      cause: safeStringify(result.raw),
      code: "UNKNOWN_ERROR",
      hint: "Fluent mutations expose count or affectedRows from adapter meta (PostgreSQL rowCount, D1 changes, Gateway envelope). Never fabricated.",
      message:
        "Expected affected row count but response.count/affectedRows is missing",
      status: result.status,
    });
  }

  const minimum = options?.min;
  if (minimum !== undefined && count < minimum) {
    throw new AthenaGatewayError({
      cause: safeStringify(result.raw),
      code: "UNKNOWN_ERROR",
      hint: contextHint(context),
      message: `Expected at least ${minimum} affected rows but received ${count}`,
      status: result.status,
    });
  }

  return count;
}

function applyBounds(
  value: number,
  options?: IntCoercionOptions
): number | null {
  if (options?.min !== undefined && value < options.min) {
    return null;
  }
  if (options?.max !== undefined && value > options.max) {
    return null;
  }
  return value;
}

function parseIntegerString(value: string): number | null {
  const normalized = value.trim();
  if (!/^[-+]?\d+$/.test(normalized)) {
    return null;
  }
  const parsed = Number(normalized);
  if (!(Number.isFinite(parsed) && Number.isInteger(parsed))) {
    return null;
  }
  return parsed;
}

/**
 * Safely coerces `unknown` values into finite integers.
 *
 * Returns `null` when coercion fails or bounds/strict bigint checks are violated.
 */
export function coerceInt(
  value: unknown,
  options?: IntCoercionOptions
): number | null {
  if (value === null) {
    return null;
  }

  if (typeof value === "number") {
    if (!(Number.isFinite(value) && Number.isInteger(value))) {
      return null;
    }
    return applyBounds(value, options);
  }

  if (typeof value === "string") {
    const parsed = parseIntegerString(value);
    if (parsed === null) {
      return null;
    }
    return applyBounds(parsed, options);
  }

  if (typeof value === "bigint") {
    if (
      options?.strictBigInt &&
      (value > BigInt(Number.MAX_SAFE_INTEGER) ||
        value < BigInt(Number.MIN_SAFE_INTEGER))
    ) {
      return null;
    }
    const parsed = Number(value);
    if (!(Number.isFinite(parsed) && Number.isInteger(parsed))) {
      return null;
    }
    return applyBounds(parsed, options);
  }

  return null;
}

/**
 * Strict integer assertion wrapper around `coerceInt`.
 *
 * Throws a `TypeError` with the provided label when coercion fails.
 */
export function assertInt(
  value: unknown,
  label = "value",
  options?: IntCoercionOptions
): number {
  const parsed = coerceInt(value, options);
  if (parsed === null) {
    throw new TypeError(`${label} must be a finite integer`);
  }
  return parsed;
}

export {
  withRetry,
  type RetryBackoffStrategy,
  type RetryConfig,
} from "./auxiliaries/retry.ts";
